/**
 * Dashboard insights helper functions
 * Pure functions - no Supabase, no React
 */

import type { StatusLevel } from '@/types/dashboard';

// Default 30% of net sales for inventory spend target (future configurable per company/location)
export const INVENTORY_SPEND_TARGET_PERCENT = 0.30;

// Default targets for labor and food cost as percentage of sales
export const LABOR_TARGET_PERCENT = 30;
export const FOOD_COST_TARGET_PERCENT = 30;

// Payroll tax rate (employer share of FICA)
export const PAYROLL_TAX_RATE = 0.153; // 15.3%

/**
 * Determine status based on value vs target and trend
 * @param currentValue - Current metric value
 * @param target - Target value
 * @param previousValue - Previous period value for trend comparison
 * @param higherIsBetter - If true, higher values are better (e.g., revenue); if false, lower is better (e.g., costs)
 */
export function determineStatus(
  currentValue: number,
  target: number,
  previousValue: number,
  higherIsBetter: boolean = false
): StatusLevel {
  const exceedsTarget = higherIsBetter 
    ? currentValue < target * 0.9 
    : currentValue > target * 1.1;
  const trendingWrong = higherIsBetter
    ? currentValue < previousValue
    : currentValue > previousValue;

  if (exceedsTarget) return 'action';
  if (trendingWrong) return 'attention';
  return 'good';
}

/**
 * Generate recommendation text based on status and metric
 */
export function generateRecommendation(
  metric: string,
  status: StatusLevel,
  value: number,
  change: number
): string {
  const changeDirection = change >= 0 ? 'up' : 'down';
  const absChange = Math.abs(change).toFixed(1);

  switch (metric) {
    case 'labor':
      if (status === 'action') {
        return `Consider adjusting weekday staffing or shift coverage.`;
      } else if (status === 'attention') {
        return `Consider adjusting weekday staffing or shift coverage.`;
      }
      return `Continue current staffing levels.`;

    case 'foodCost':
      if (status === 'good') {
        return `Continue current portioning and vendor pricing.`;
      }
      return `Review portion sizes and vendor pricing.`;

    case 'inventory':
      if (status === 'action') {
        return `Review this week's orders before placing with Sysco.`;
      } else if (status === 'attention') {
        return `Review this week's orders before placing with Sysco.`;
      }
      return `Inventory spending is on track.`;

    default:
      return '';
  }
}

/**
 * Format currency for display
 */
export function formatCurrencyCompact(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  }
  return `$${value.toFixed(0)}`;
}

/**
 * Calculate percentage change between two values
 */
export function calculatePercentChange(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
}

/**
 * Determine overall status from an array of individual statuses
 */
export function determineOverallStatus(statuses: StatusLevel[]): StatusLevel {
  if (statuses.includes('action')) return 'action';
  if (statuses.includes('attention')) return 'attention';
  return 'good';
}

/**
 * Generate headline based on overall status
 */
export function generateHeadline(status: StatusLevel): string {
  switch (status) {
    case 'action':
      return 'Action needed this week';
    case 'attention':
      return 'Keep an eye on things';
    case 'good':
      return 'Everything looks good';
  }
}

/**
 * Generate subheadline based on status and context
 */
export function generateSubheadline(
  status: StatusLevel,
  laborPercent: number,
  foodPercent: number
): string {
  switch (status) {
    case 'action':
      if (laborPercent > LABOR_TARGET_PERCENT) {
        return `Labor costs at ${laborPercent.toFixed(1)}% need attention`;
      }
      if (foodPercent > FOOD_COST_TARGET_PERCENT) {
        return `Food costs at ${foodPercent.toFixed(1)}% need attention`;
      }
      return 'Review your key metrics';
    case 'attention':
      return 'Monitor trends closely this week';
    case 'good':
      return 'Your restaurant is performing well';
  }
}
